import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Pin, PinOff, Plus, Trash2, Bold, Italic, Underline, Pencil, Check, Loader2, History, Eye, EyeOff } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { condoApi } from '../../../services/api';
import { logger } from '../../../services/logger';
import toast from 'react-hot-toast';
import { LembreteHistoryModal } from './LembreteHistoryModal';

/**
 * "Lembretes" (reminders) — pinned notes shared between all operators of a customer,
 * persisted on the backend. Formatting uses the browser's classic `document.execCommand`
 * API — deprecated but still supported everywhere Electron/Chromium runs, and is the
 * fastest path to a bold/italic/font-size toolbar without pulling in a WYSIWYG dependency.
 */

interface LembreteItem {
  id: string;
  content_html: string;
}

interface LembretesBannerProps {
  customerId: string | undefined;
  lightTheme?: boolean;
  // Manually opens the banner while it's empty (e.g. to add the first reminder). Ignored
  // once there's at least one lembrete — at that point the banner forces itself visible
  // and non-dismissible, see `visible` below.
  open: boolean;
  // Only meaningful while empty — closes the manually-opened empty banner. There is no way
  // to close it once populated (no prop, no button); shared reminders must not be dismissible.
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

/**
 * Where a collapsed banner is remembered.
 *
 * Per customer: a porteiro who folded away one condo's reminders has said nothing about the
 * next condo's, and silently hiding those would be exactly the failure the banner exists to
 * prevent. The cards fold; the header, with its count, never does.
 */
const collapsedKey = (customerId: string) => `panther.lembretes.collapsed.${customerId}`;

const readCollapsed = (customerId: string | undefined): boolean => {
  if (!customerId) return false;
  try {
    return window.localStorage.getItem(collapsedKey(customerId)) === '1';
  } catch {
    return false;
  }
};

export const LembretesBanner: React.FC<LembretesBannerProps> = ({ customerId, lightTheme = false, open, onClose, onCountChange }) => {
  const [lembretes, setLembretes] = useState<LembreteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readCollapsed(customerId));
  const editableRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Switching customers re-reads that customer's own choice.
  useEffect(() => { setCollapsed(readCollapsed(customerId)); }, [customerId]);

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (next && editingId) {
      saveEditable(editingId);
      setEditingId(null);
    }
    if (!customerId) return;
    try {
      if (next) window.localStorage.setItem(collapsedKey(customerId), '1');
      else window.localStorage.removeItem(collapsedKey(customerId));
    } catch { /* a browser refusing storage is no reason to refuse the toggle */ }
  };

  useEffect(() => {
    onCountChange?.(lembretes.length);
  }, [lembretes.length, onCountChange]);

  const fetchLembretes = useCallback(async () => {
    if (!customerId) {
      setLembretes([]);
      return;
    }
    setLoading(true);
    try {
      const response = await condoApi.getLembretes(customerId);
      setLembretes(response.data?.data || []);
    } catch (err) {
      logger.error('LembretesBanner', 'Error fetching lembretes', err as Error);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchLembretes();
  }, [fetchLembretes]);

  // Entering edit mode only flips `contentEditable` on — it doesn't move DOM focus there.
  // Without this, focus stays on whatever was last focused (typically the pencil button
  // that was just clicked), so the very first keystroke gets picked up by other
  // document-level key listeners (e.g. QuickSearchBar's "type anywhere to search") instead
  // of landing in the reminder text.
  useEffect(() => {
    if (!editingId) return;
    const el = editableRefs.current[editingId];
    if (!el) return;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [editingId]);

  const saveEditable = async (id: string) => {
    const el = editableRefs.current[id];
    if (!el || !customerId) return;
    const html = el.innerHTML;
    setLembretes(prev => prev.map(l => (l.id === id ? { ...l, content_html: html } : l)));
    try {
      await condoApi.updateLembrete(customerId, id, html);
    } catch (err) {
      logger.error('LembretesBanner', 'Error saving lembrete', err as Error);
      toast.error('Erro ao salvar lembrete');
    }
  };

  const handleAdd = async () => {
    if (!customerId) return;
    try {
      const response = await condoApi.createLembrete(customerId, '');
      const created: LembreteItem = response.data?.data;
      setLembretes(prev => [...prev, created]);
      setEditingId(created.id);
    } catch (err) {
      logger.error('LembretesBanner', 'Error creating lembrete', err as Error);
      toast.error('Erro ao criar lembrete');
    }
  };

  const handleDelete = async (id: string) => {
    if (!customerId) return;
    const previous = lembretes;
    setLembretes(prev => prev.filter(l => l.id !== id));
    if (editingId === id) setEditingId(null);
    try {
      await condoApi.deleteLembrete(customerId, id);
    } catch (err) {
      logger.error('LembretesBanner', 'Error deleting lembrete', err as Error);
      toast.error('Erro ao excluir lembrete');
      setLembretes(previous);
    }
  };

  const toggleEdit = (id: string) => {
    if (editingId === id) {
      saveEditable(id);
      setEditingId(null);
    } else {
      if (editingId) saveEditable(editingId);
      setEditingId(id);
    }
  };

  // onMouseDown + preventDefault keeps the current text selection alive in the
  // contentEditable — otherwise clicking a toolbar button steals focus first and
  // execCommand ends up with nothing selected to format.
  const format = (cmd: string, value?: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.execCommand(cmd, false, value);
  };

  const cardBg = lightTheme ? 'bg-white border-amber-300' : 'bg-black/20 border-amber-500/30';
  const toolbarBtn = cn(
    'p-1.5 rounded hover:bg-black/10 transition-colors',
    lightTheme ? 'text-amber-800' : 'text-amber-300'
  );

  // Forced visible (and non-dismissible, see the header below) once there's at least one
  // lembrete. `open` only controls visibility of the empty state, before anything exists.
  const hasLembretes = lembretes.length > 0;
  // Only a populated banner folds — there is nothing to fold away from the empty state, and
  // folding it would leave a header offering a "Mostrar" that reveals nothing.
  const isFolded = collapsed && hasLembretes;
  if (!customerId) return null;
  // Don't flash the banner shell before the initial fetch resolves — only render eagerly
  // if the caller explicitly asked to open it (e.g. to add the first reminder).
  if (loading && !open) return null;
  if (!open && !hasLembretes) return null;

  return (
    <div
      className={cn(
        'flex-shrink-0 border-b-2 px-3 py-2',
        lightTheme ? 'bg-amber-50 border-amber-400' : 'bg-amber-500/10 border-amber-500/50'
      )}
    >
      <div className={cn('flex items-center gap-2', !isFolded && 'mb-2')}>
        <Pin size={16} className={cn('rotate-45 shrink-0', lightTheme ? 'text-amber-700' : 'text-amber-500')} />
        <span className={cn('text-xs font-bold uppercase tracking-wide', lightTheme ? 'text-amber-800' : 'text-amber-400')}>
          Lembretes
        </span>
        {/* The count rides on the header so a folded banner still states what is waiting */}
        {hasLembretes && (
          <span className={cn(
            'px-1.5 py-0.5 rounded-full text-[11px] font-bold leading-none',
            lightTheme ? 'bg-amber-200 text-amber-900' : 'bg-amber-500/30 text-amber-200'
          )}>
            {lembretes.length}
          </span>
        )}
        {loading && <Loader2 size={12} className={cn('animate-spin', lightTheme ? 'text-amber-700' : 'text-amber-400')} />}
        <button
          onClick={handleAdd}
          className={cn('flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ml-2', lightTheme ? 'bg-amber-100 hover:bg-amber-200 text-amber-800' : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300')}
        >
          <Plus size={12} /> Novo lembrete
        </button>
        <button
          onClick={() => setShowHistory(true)}
          className={cn('flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md', lightTheme ? 'bg-amber-100 hover:bg-amber-200 text-amber-800' : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300')}
        >
          <History size={12} /> Histórico
        </button>
        {/* No way to DISMISS the banner once it has content — shared reminders must stay
            visible to every operator. Only the empty "click to add your first one" state
            (manually opened via `open`) can be closed back up. Folding the cards away is a
            different thing: the header and its count stay put. */}
        {!hasLembretes && (
          <button
            onClick={onClose}
            className={cn('ml-auto p-1 rounded hover:bg-black/5', lightTheme ? 'text-amber-700' : 'text-amber-400')}
            title="Desafixar lembretes"
          >
            <PinOff size={14} />
          </button>
        )}
        {hasLembretes && (
          <button
            onClick={toggleCollapsed}
            className={cn(
              'ml-auto flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md',
              lightTheme ? 'bg-amber-100 hover:bg-amber-200 text-amber-800' : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300'
            )}
            title={collapsed ? 'Mostrar lembretes' : 'Ocultar lembretes'}
            aria-expanded={!collapsed}
          >
            {collapsed ? <Eye size={13} /> : <EyeOff size={13} />}
            <span>{collapsed ? 'Mostrar' : 'Ocultar'}</span>
          </button>
        )}
      </div>

      {/* Grid, not flex-wrap: with flex-wrap + max-w-sm per card, a row with only 1-2
          reminders left most of the banner's width empty since each card capped its own
          growth. Must be auto-fit, NOT auto-fill: auto-fill reserves empty tracks for
          columns that fit but have no content, so 3 cards in a container wide enough for 9
          columns still only occupy 3 narrow tracks with a huge empty gap after them.
          auto-fit collapses those empty tracks to 0 and lets the 1fr stretch across just
          the cards that exist, so a handful of cards spans the full width and adding more
          reflows into additional rows on its own. */}
      {/* Bounded height: a reminder can be a twenty-name guest list, and the banner sits above
          everything the porteiro actually works in — visitors, search, events. Capped at a
          share of the viewport and scrolled inside, so a long reminder costs its own space
          and never the screen. */}
      {!isFolded && (
      <div
        data-testid="lembretes-list"
        className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(220px,1fr))] max-h-[30vh] overflow-y-auto overscroll-contain pr-0.5"
      >
        {!loading && lembretes.length === 0 && (
          <p className={cn('text-sm italic', lightTheme ? 'text-amber-700' : 'text-amber-400')}>
            Nenhum lembrete. Clique em "Novo lembrete" para adicionar.
          </p>
        )}
        {lembretes.map(item => {
          const isEditing = editingId === item.id;
          return (
            <div key={item.id} className={cn('rounded-lg border px-2.5 py-2', cardBg)}>
              {isEditing && (
                <div className="flex items-center gap-0.5 mb-1.5 border-b border-amber-500/20 pb-1.5">
                  <button onMouseDown={format('bold')} className={toolbarBtn} title="Negrito"><Bold size={13} /></button>
                  <button onMouseDown={format('italic')} className={toolbarBtn} title="Itálico"><Italic size={13} /></button>
                  <button onMouseDown={format('underline')} className={toolbarBtn} title="Sublinhado"><Underline size={13} /></button>
                  <button onMouseDown={format('fontSize', '5')} className={cn(toolbarBtn, 'text-sm font-bold px-2')} title="Aumentar fonte">A+</button>
                  <button onMouseDown={format('fontSize', '2')} className={cn(toolbarBtn, 'text-xs px-2')} title="Diminuir fonte">A-</button>
                </div>
              )}
              <div className="flex items-start gap-1.5">
                <div
                  ref={el => { editableRefs.current[item.id] = el; }}
                  contentEditable={isEditing}
                  suppressContentEditableWarning
                  onBlur={() => isEditing && saveEditable(item.id)}
                  dangerouslySetInnerHTML={{ __html: item.content_html || (isEditing ? '' : '<span class="opacity-50">Vazio — clique no lápis para editar</span>') }}
                  className={cn(
                    'flex-1 text-sm whitespace-pre-line leading-snug outline-none min-h-[1.5em]',
                    lightTheme ? 'text-amber-900' : 'text-amber-100',
                    isEditing && (lightTheme ? 'bg-amber-50/50 rounded px-1' : 'bg-black/20 rounded px-1')
                  )}
                />
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button onClick={() => toggleEdit(item.id)} className={toolbarBtn} title={isEditing ? 'Concluir' : 'Editar'}>
                    {isEditing ? <Check size={13} /> : <Pencil size={13} />}
                  </button>
                  <button onClick={() => handleDelete(item.id)} className={cn(toolbarBtn, 'hover:text-red-600')} title="Excluir">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}

      <LembreteHistoryModal
        open={showHistory}
        onClose={() => setShowHistory(false)}
        customerId={customerId}
        lightTheme={lightTheme}
      />
    </div>
  );
};
